import {
    FormControl,
    FormHelperText,
    InputLabel,
    MenuItem,
    SelectChangeEvent,
    Typography,
} from '@mui/material';
import { useRef, useState } from 'react';
import { SubmitHandler, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import useAdminApi from '../../../../hooks/admin/useAdminApi';
import useDefaultStockSWR from '../../../../hooks/useDefaultStock';
import SnackbarUtils from '../../../../utils/SnackbarUtils';
import { PATTERNS } from '../../../../utils/constans/constans';
import PrimaryBtn from '../../../shared/Buttons/PrimaryButton';
import SelectStyled from '../../../shared/SelectStyled';
import TextFieldStyled from '../../../shared/TextFieldStyled';
import { AuthPageTitle } from '../../../shared/Typography/AuthenticationsPageTitle';
import { FormControlsWrapper } from '../../../styled/Form/FormControlsWrapper';
import { SubmitButtonWrapper } from '../../../styled/Form/SubmitButtonWrapper';

export const AdminAddPartner = () => {
    const {
        register,
        handleSubmit,
        watch,
        formState: { errors },
    } = useForm<FormValues>();

    //get default stock list
    const { stocks, isLoadingStock, isErrorStock } = useDefaultStockSWR();
    //country list
    // const { countries } = useCountriesSWR();
    const adminApi = useAdminApi();
    const { t } = useTranslation();
    //handle default stock change
    const [defaultStock, setDefaultStock] = useState('');
    const [country, setCountry] = useState('HU');

    const handleChange = (event: SelectChangeEvent) => {
        setDefaultStock(event.target.value as string);
    };
    const handleCountryChange = (event: SelectChangeEvent) => {
        setCountry(event.target.value as string);
    };

    const [isLoading, setIsLoading] = useState(false);

    //handle code change
    const bc_code = useRef('');
    bc_code.current = watch('bc_customer_no', '');

    const onSubmit: SubmitHandler<FormValues> = (data) => {
        setIsLoading(true);
        adminApi
            .sendPartnerRegistration(data)
            .then(() => {
                SnackbarUtils.success(
                    t('NOTIFICATION.ADD_PARTNER_SUCCESS').toString()
                );
            })
            .finally(() => {
                setIsLoading(false);
            });
    };

    if (isLoadingStock) {
        return <></>;
    }

    if (isErrorStock) {
        return <>{t('NOTIFICATION.ERROR_LOADING')}</>;
    }

    return (
        <>
            <AuthPageTitle>
                {t('ADD_PARTNER.TITLE', {
                    ns: 'admin',
                }).toString()}
            </AuthPageTitle>
            <Typography paragraph>
                {t('ADD_PARTNER.SHORT_DESC', {
                    ns: 'admin',
                }).toString()}
            </Typography>
            <form onSubmit={handleSubmit(onSubmit)}>
                <Typography paragraph>
                    {t('REGISTRATION_FORM_FIELDS.PROFILE_FORMS').toString()}
                </Typography>
                {/* Email address */}
                <FormControl fullWidth>
                    <TextFieldStyled
                        label={t('REGISTRATION_FORM_FIELDS.EMAIL').toString()}
                        error={!!errors.email}
                        {...register('email', {
                            required: t(
                                'FORM_VALIDATION_ERROR.REQUIRED'
                            ).toString(),
                            pattern: {
                                value: PATTERNS.VALID_EMAIL,
                                message: t(
                                    'FORM_VALIDATION_ERROR.EMAIL'
                                ).toString(),
                            },
                        })}
                        helperText={errors.email ? errors.email.message : ' '}
                    />
                </FormControl>
                {/* Company */}
                <FormControl fullWidth>
                    <TextFieldStyled
                        label={t('REGISTRATION_FORM_FIELDS.COMPANY').toString()}
                        error={!!errors.company}
                        {...register('company', {
                            required: t(
                                'FORM_VALIDATION_ERROR.REQUIRED'
                            ).toString(),
                        })}
                        helperText={
                            errors.company ? errors.company.message : ' '
                        }
                    />
                </FormControl>
                <FormControlsWrapper>
                    {/* Last Name */}
                    <FormControl fullWidth>
                        <TextFieldStyled
                            label={t(
                                'REGISTRATION_FORM_FIELDS.LAST_NAME'
                            ).toString()}
                            error={!!errors.last_name}
                            {...register('last_name', {
                                required: t(
                                    'FORM_VALIDATION_ERROR.REQUIRED'
                                ).toString(),
                                minLength: {
                                    value: 3,
                                    message: t(
                                        'FORM_VALIDATION_ERROR.NAME_MIN_LENGTH'
                                    ).toString(),
                                },
                            })}
                            helperText={
                                errors.last_name
                                    ? errors.last_name.message
                                    : ' '
                            }
                        />
                    </FormControl>
                    {/* First Name */}
                    <FormControl fullWidth>
                        <TextFieldStyled
                            label={t(
                                'REGISTRATION_FORM_FIELDS.FIRST_NAME'
                            ).toString()}
                            error={!!errors.first_name}
                            {...register('first_name', {
                                required: t(
                                    'FORM_VALIDATION_ERROR.REQUIRED'
                                ).toString(),
                                minLength: {
                                    value: 3,
                                    message: t(
                                        'FORM_VALIDATION_ERROR.NAME_MIN_LENGTH'
                                    ).toString(),
                                },
                            })}
                            helperText={
                                errors.first_name
                                    ? errors.first_name.message
                                    : ' '
                            }
                        />
                    </FormControl>
                </FormControlsWrapper>
                {/* Phone */}
                <FormControl fullWidth>
                    <TextFieldStyled
                        label={t('REGISTRATION_FORM_FIELDS.PHONE').toString()}
                        error={!!errors.phone}
                        {...register('phone', {
                            required: t(
                                'FORM_VALIDATION_ERROR.REQUIRED'
                            ).toString(),
                            pattern: {
                                value: PATTERNS.PHONE_NUMBER,
                                message: t(
                                    'FORM_VALIDATION_ERROR.PHONE'
                                ).toString(),
                            },
                        })}
                        helperText={errors.phone ? errors.phone.message : ' '}
                    />
                </FormControl>
                {/* Tax number */}
                <FormControl fullWidth>
                    <TextFieldStyled
                        label={t(
                            'REGISTRATION_FORM_FIELDS.TAX_NUMBER'
                        ).toString()}
                        error={!!errors.tax_number}
                        {...register('tax_number', {
                            required: t(
                                'FORM_VALIDATION_ERROR.REQUIRED'
                            ).toString(),
                            pattern: {
                                value: PATTERNS.HUN_TAX_NUMBER,
                                message: t(
                                    'FORM_VALIDATION_ERROR.TAX_NUMBER_FORMAT'
                                ).toString(),
                            },
                        })}
                        helperText={
                            errors.tax_number ? errors.tax_number.message : ' '
                        }
                    />
                </FormControl>
                {/* BC code */}
                <FormControl fullWidth>
                    <TextFieldStyled
                        label={t('REGISTRATION_FORM_FIELDS.BC_CODE').toString()}
                        {...register('bc_customer_no')}
                        helperText={' '}
                    />
                </FormControl>
                {/* BC Company number */}
                {bc_code.current != '' && (
                    <FormControl fullWidth>
                        <TextFieldStyled
                            label={t(
                                'REGISTRATION_FORM_FIELDS.BC_COMPANY_NUMBER'
                            ).toString()}
                            error={!!errors.bc_company_number}
                            {...register('bc_company_number', {
                                required: t(
                                    'FORM_VALIDATION_ERROR.REQUIRED'
                                ).toString(),
                            })}
                            helperText={
                                errors.bc_company_number
                                    ? errors.bc_company_number.message
                                    : ' '
                            }
                        />
                    </FormControl>
                )}
                {/* Default stock */}
                <FormControl fullWidth variant="filled">
                    <InputLabel
                        sx={{
                            color: '#999999',
                        }}
                    >
                        {t('REGISTRATION_FORM_FIELDS.DEFAULT_STOCK').toString()}
                    </InputLabel>
                    <SelectStyled
                        label={t(
                            'REGISTRATION_FORM_FIELDS.DEFAULT_STOCK'
                        ).toString()}
                        {...register('default_stock', {
                            required: t(
                                'FORM_VALIDATION_ERROR.REQUIRED'
                            ).toString(),
                            onChange: (e) => handleChange(e),
                        })}
                        value={defaultStock}
                    >
                        {stocks.map((stock) => (
                            <MenuItem
                                key={stock.bc_location_code}
                                value={stock.bc_location_code}
                            >
                                {stock.bc_location_name}
                            </MenuItem>
                        ))}
                    </SelectStyled>
                    <FormHelperText error={!!errors.default_stock}>
                        &#8203;
                        {errors.default_stock && errors.default_stock.message}
                    </FormHelperText>
                </FormControl>
                <Typography paragraph>
                    {t(
                        'REGISTRATION_FORM_FIELDS.DEFAULT_SHIPPING_FORMS'
                    ).toString()}
                </Typography>
                {/* Country */}
                <FormControl fullWidth variant="filled">
                    <InputLabel
                        sx={{
                            color: '#999999',
                        }}
                    >
                        {t('REGISTRATION_FORM_FIELDS.COUNTRY').toString()}
                    </InputLabel>
                    <SelectStyled
                        label={t('REGISTRATION_FORM_FIELDS.COUNTRY').toString()}
                        {...register('country', {
                            required: t(
                                'FORM_VALIDATION_ERROR.REQUIRED'
                            ).toString(),
                            onChange: (e) => handleCountryChange(e),
                        })}
                        value={country}
                    >
                        {/*{countries &&*/}
                        {/*    countries.map((countryCode) => (*/}
                        {/*        <MenuItem key={countryCode} value={countryCode}>*/}
                        {/*            {t('COUNTRIES.' + countryCode)}*/}
                        {/*        </MenuItem>*/}
                        {/*    ))}*/}
                        <MenuItem value={'HU'}>
                            {t('COUNTRIES.HU', { ns: 'countries' })}
                        </MenuItem>
                    </SelectStyled>
                    <FormHelperText error={!!errors.country}>
                        &#8203;
                        {errors.country && errors.country.message}
                    </FormHelperText>
                </FormControl>
                <FormControlsWrapper>
                    {/* Postcode */}
                    <FormControl fullWidth>
                        <TextFieldStyled
                            label={t(
                                'REGISTRATION_FORM_FIELDS.POSTAL_CODE'
                            ).toString()}
                            error={!!errors.postcode}
                            {...register('postcode', {
                                required: t(
                                    'FORM_VALIDATION_ERROR.REQUIRED'
                                ).toString(),
                            })}
                            helperText={
                                errors.postcode ? errors.postcode.message : ' '
                            }
                        />
                    </FormControl>
                    {/* City */}
                    <FormControl fullWidth>
                        <TextFieldStyled
                            label={t(
                                'REGISTRATION_FORM_FIELDS.CITY'
                            ).toString()}
                            error={!!errors.city}
                            {...register('city', {
                                required: t(
                                    'FORM_VALIDATION_ERROR.REQUIRED'
                                ).toString(),
                            })}
                            helperText={errors.city ? errors.city.message : ' '}
                        />
                    </FormControl>
                </FormControlsWrapper>
                {/* Address 1 */}
                <FormControl fullWidth>
                    <TextFieldStyled
                        label={t(
                            'REGISTRATION_FORM_FIELDS.ADDRESS1'
                        ).toString()}
                        error={!!errors.address1}
                        {...register('address1', {
                            required: t(
                                'FORM_VALIDATION_ERROR.REQUIRED'
                            ).toString(),
                        })}
                        helperText={
                            errors.address1 ? errors.address1.message : ' '
                        }
                    />
                </FormControl>
                {/* Address 2 */}
                <FormControl fullWidth>
                    <TextFieldStyled
                        label={t(
                            'REGISTRATION_FORM_FIELDS.ADDRESS2'
                        ).toString()}
                        {...register('address2')}
                        helperText={' '}
                    />
                </FormControl>
                {/* Password */}
                {/*<Typography paragraph>*/}
                {/*    {t('REGISTRATION_FORM_FIELDS.PASSWORD_FORMS').toString()}*/}
                {/*</Typography>*/}
                {/*<FormControl fullWidth>*/}
                {/*    <TextFieldStyled*/}
                {/*        label={t(*/}
                {/*            'REGISTRATION_FORM_FIELDS.PASSWORD'*/}
                {/*        ).toString()}*/}
                {/*        error={!!errors.password}*/}
                {/*        type="password"*/}
                {/*        {...register('password', {*/}
                {/*            required: t(*/}
                {/*                'FORM_VALIDATION_ERROR.REQUIRED'*/}
                {/*            ).toString(),*/}
                {/*            minLength: {*/}
                {/*                value: 5,*/}
                {/*                message: t(*/}
                {/*                    'FORM_VALIDATION_ERROR.PASSWORD_MIN_LENGTH'*/}
                {/*                ).toString(),*/}
                {/*            },*/}
                {/*        })}*/}
                {/*        helperText={*/}
                {/*            errors.password ? errors.password.message : ' '*/}
                {/*        }*/}
                {/*    />*/}
                {/*</FormControl>*/}
                {/* Retry Password */}
                {/*<FormControl fullWidth>*/}
                {/*    <TextFieldStyled*/}
                {/*        label={t(*/}
                {/*            'REGISTRATION_FORM_FIELDS.RETRY_PASSWORD'*/}
                {/*        ).toString()}*/}
                {/*        error={!!errors.password_confirmation}*/}
                {/*        type="password"*/}
                {/*        {...register('password_confirmation', {*/}
                {/*            required: t(*/}
                {/*                'FORM_VALIDATION_ERROR.REQUIRED'*/}
                {/*            ).toString(),*/}
                {/*            validate: (value) =>*/}
                {/*                value === password.current ||*/}
                {/*                t(*/}
                {/*                    'FORM_VALIDATION_ERROR.PASSWORD_MISMATCH'*/}
                {/*                ).toString(),*/}
                {/*        })}*/}
                {/*        helperText={*/}
                {/*            errors.password_confirmation*/}
                {/*                ? errors.password_confirmation.message*/}
                {/*                : ' '*/}
                {/*        }*/}
                {/*    />*/}
                {/*</FormControl>*/}
                {/* Note */}
                <Typography paragraph>
                    {t('REGISTRATION_FORM_FIELDS.NOTE').toString()}
                </Typography>
                <FormControl fullWidth>
                    <TextFieldStyled
                        label={t(
                            'REGISTRATION_FORM_FIELDS.NOTE_DESC'
                        ).toString()}
                        multiline
                        rows={4}
                        {...register('note')}
                    />
                </FormControl>
                {/* Submit */}
                <SubmitButtonWrapper>
                    <PrimaryBtn type="submit" disabled={isLoading}>
                        {bc_code.current.length == 0 &&
                            t('ADD_PARTNER.ADD_NEW', {
                                ns: 'admin',
                            })}
                        {bc_code.current.length > 0 &&
                            t('ADD_PARTNER.CONNECT_EXIST', {
                                ns: 'admin',
                            })}
                    </PrimaryBtn>
                </SubmitButtonWrapper>
            </form>
        </>
    );
};

type FormValues = {
    first_name: string;
    last_name: string;
    email: string;
    phone: string;
    company: string;
    tax_number: string;
    default_stock: string;
    // password: string;
    // password_confirmation: string;
    // terms_of_service: boolean;
    // has4322: boolean;
    country: string;
    postcode: string;
    city: string;
    address1: string;
    address2: string;
    note: string;
    bc_customer_no: string;
    bc_company_number: string;
};
